// Render node definitions — shape and material graph nodes.
// These are editor-facing metadata only (ports, defaults, UI schema);
// evaluation lives in the engine (crates/graph/src/nodes.rs). The
// RenderEvalContext shape stays here as the TS mirror of the engine's
// EvalContext (crates/graph/src/ctx.rs) for the wasm boundary.

import { geometryNodeKindsJson, nodeDefaultsJson } from 'fluveria-engine-wasm';
import { MATH_NODE_DEFS } from './nodeDefsMath.js';
import { SDF_NODE_DEFS } from './nodeDefsSdf.js';
import { SOURCE_NODE_DEFS } from './nodeDefsSource.js';
import { SPECIAL_NODE_DEFS } from './nodeDefsSpecial.js';
import { TEXT_NODE_DEFS } from './nodeDefsText.js';
import { VECTOR_NODE_DEFS } from './nodeDefsVector.js';
import { TRANSFORM_NODE_DEFS } from './nodeDefsTransform.js';
import { BOUNDS_NODE_DEFS } from './nodeDefsBounds.js';
import type { NodeKind, NodeDefBase, NodeDataByKind, PortDef } from './nodeDefs.js';
import type { ArcCoverageMode } from '../sdf/arcCoverageMode.js';

export interface RenderEvalContext {
  samplePoint: [number, number];
  layerPoint?: [number, number];
  shapePoint?: [number, number];
  layerSize?: [number, number];
  shapeUnit?: 'px' | 'mm';
  colormap?: 'gray' | 'inferno' | 'plasma' | 'turbo';
  distance?: number;
  coverage?: number;
  layoutBounds?: [number, number, number, number];
  contourBounds?: [number, number, number, number];
  transform?: number[];
  /** Pixels per normalized material sample unit in X. */
  pixelsPerSampleUnitX?: number;
  /** Pixels per normalized material sample unit in Y. */
  pixelsPerSampleUnitY?: number;
  /** Pixels per isotropic material sample unit. */
  pixelsPerSampleUnit?: number;
  /** Pixels per shape unit in X. */
  pixelsPerShapeUnitX?: number;
  /** Pixels per shape unit in Y. */
  pixelsPerShapeUnitY?: number;
  /** Pixels per isotropic shape unit. */
  pixelsPerShapeUnit?: number;
  /** AA mode for mask nodes (Mask). */
  aaMode?: 'none' | 'fwidth' | 'msaa2x';
  /** Debug mode for Text/Icon geometry coverage. */
  arcCoverageMode?: ArcCoverageMode;
  /** AA feather width in shape space. */
  aaFeatherWidth?: number;
  /** Active viewport render theme — mode 0/1/2 and resolved theme 0/1 (light/dark). */
  renderTheme?: { mode: number; resolvedTheme: number };
}

export interface RenderNodeDef<K extends NodeKind = NodeKind> extends NodeDefBase {
  /** Injected from the engine's node_defaults.json — do not author in TS. */
  defaults?: NodeDataByKind[K];
  /** Which graph slots this node is available in. Omit to allow in geometry+material slots. */
  graphs?: ('geometry' | 'material')[];
}

export type RenderNodeDefs = { [K in NodeKind]: RenderNodeDef<K> };
export type RenderNodeDefsSubset<K extends NodeKind> = { [P in K]: RenderNodeDef<P> };

/** Define a render node (editor metadata; evaluation lives in the engine). */
export function defineRenderNode<const I extends readonly PortDef[]>(
  def: Omit<RenderNodeDef, 'inputs'> & { inputs: I },
): RenderNodeDef<any> {
  return def as unknown as RenderNodeDef<any>;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const RENDER_NODE_DEFS: Record<string, RenderNodeDef<any>> = {
  ...SOURCE_NODE_DEFS,
  ...MATH_NODE_DEFS,
  ...VECTOR_NODE_DEFS,
  ...TRANSFORM_NODE_DEFS,
  ...SDF_NODE_DEFS,
  ...TEXT_NODE_DEFS,
  ...BOUNDS_NODE_DEFS,
  ...SPECIAL_NODE_DEFS,
};

// Node data defaults are owned by the engine (single source: the v2 codec
// omits fields equal to them). Injected lazily — this module loads before
// the wasm engine is initialized, and defaults are only read afterwards
// (node creation, graph normalization on file load).
let nodeDefaultsTable: Record<string, Record<string, unknown>> | null = null;
function engineNodeDefaults(type: string): Record<string, unknown> {
  nodeDefaultsTable ??= JSON.parse(nodeDefaultsJson()) as Record<string, Record<string, unknown>>;
  const defaults = nodeDefaultsTable[type];
  if (!defaults) throw new Error(`engine node_defaults.json is missing type '${type}'`);
  return defaults;
}
for (const [type, def] of Object.entries(RENDER_NODE_DEFS)) {
  Object.defineProperty(def, 'defaults', {
    get: () => engineNodeDefaults(type),
    enumerable: true,
    configurable: true,
  });
}

// The geometry allowlist is owned by the engine too (allowed_in_geometry is
// the compile gate; GEOMETRY_NODE_KINDS is its exported form). Same lazy
// pattern as defaults: read only after wasm init.
let geometryKinds: Set<string> | null = null;
function engineAllowsInGeometry(type: string): boolean {
  geometryKinds ??= new Set(JSON.parse(geometryNodeKindsJson()) as string[]);
  return geometryKinds.has(type);
}

// Math/vector defs derive their graph availability from the engine list:
// statically-evaluable kinds appear in both graphs, the rest are material-only.
for (const [type, def] of [...Object.entries(MATH_NODE_DEFS), ...Object.entries(VECTOR_NODE_DEFS)]) {
  Object.defineProperty(def, 'graphs', {
    get: () => (engineAllowsInGeometry(type) ? ['geometry', 'material'] : ['material']),
    enumerable: true,
    configurable: true,
  });
}
